import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
import {
  ShieldCheck, // Customer Schema / Schema Enforcement
  SquareCheckBig, // MetaRouter Schema
  Package,      // Vendor Schemas
  ArrowRight,   // For arrows in the diagram
} from 'lucide-react';
import { LucideIcon } from 'lucide-react'; // Import LucideIcon type
import { Pipelines, Destinations } from '../icons'; // Import custom icons
import clsx from 'clsx';

console.log({ Pipelines, Destinations });

// Define the structure for each step in the diagram
interface DiagramStep {
  id: string;
  title: string;
  icon: React.ElementType | LucideIcon; // Allow both React.ElementType and LucideIcon
  description: string;
  link: string;
  isOptional?: boolean;
  jsonSchemaRelated?: boolean;
  toolLabel: string;
  toolIcon: React.ElementType | LucideIcon;
  toolType: string;
  subtitle: string; // Subtitle for the explanation section
}

const diagramSteps: DiagramStep[] = [
  {
    id: 'customer-schema',
    title: 'Customer Schemas',
    icon: ShieldCheck,
    subtitle: 'Know what’s coming in',
    description: `
      <p>Define the raw event data captured from the customer’s properties. This helps guide downstream transformation decisions and gives all teams a clear view of incoming structure.</p>
      <p>Capture events using the MetaRouter Chrome Extension and import them directly into this tool for faster, more accurate schema creation.</p>
    `,
    link: '/labs/schema-enforcement',
    jsonSchemaRelated: true,
    toolLabel: 'Schema Enforcement',
    toolIcon: ShieldCheck,
    toolType: 'JSON Schema Editor',
  },
  {
    id: 'pipeline-transforms',
    title: 'Pipeline Transforms',
    icon: Pipelines,
    subtitle: 'Shape the data to fit your use case',
    description: `
      <p>A flexible layer for applying event transformations: schema alignment, bot filtering, data filtering, obfuscation, and normalization.</p>
      <p class="mt-4 text-sm text-gray-600 dark:text-gray-400">→ Raw JSON in → Clean, structured JSON out.</p>
    `,
    link: '/labs/pipelines',
    toolLabel: 'Pipeline Transforms',
    toolIcon: Pipelines,
    toolType: 'YAML Playbook Editor',
  },
  {
    id: 'metarouter-schema',
    title: 'Pipeline Schemas',
    icon: SquareCheckBig,
    subtitle: 'The bridge between customer data and our Starter Kits',
    description: `
      <p>Pipeline Schemas define the expected outcome of Pipeline Transform layer, and serve as a bridge between the customer data and integrations. There are two core use cases:</p>
      <ul class="list-disc list-inside pl-5">
        <li class="mb-3"><strong>MetaRouter Spec (AJS-based):</strong> These are official, maintained fixtures used by the Integrations team to build Starter Kits. If the customer can align to this spec, integrations require minimal setup.</li>
        <li class="mb-2"><strong>Custom Spec (Enforced Schema):</strong> If a customer needs to retain their own data format, this schema acts as the enforcement layer. Integrators define the desired structure to ensure Pipeline Transform outuput is consistent and compliant.</li>
      </ul>
      <p>In both cases, this schema becomes the critical reference point for shaping and validating event data in your pipeline.</p>
    `,
    link: '/labs/contracts',
    isOptional: true,
    jsonSchemaRelated: true,
    toolLabel: 'Data Contracts',
    toolIcon: SquareCheckBig,
    toolType: 'JSON Schema Editor',
  },
  {
    id: 'vendor-transforms',
    title: 'Vendor Transforms',
    icon: Destinations,
    subtitle: 'Smarter, faster vendor mapping',
    description: `
      <p>An enhanced version of our current playbook editor with auto-complete, live schema previews, feedback, and AI-assisted creation of mappings, filter rules, and Lua expressions.</p>
    `,
    link: '/labs/integrations',
    toolLabel: 'Vendor Transforms',
    toolIcon: Destinations,
    toolType: 'YAML Playbook Editor',
  },
  {
    id: 'vendor-schemas',
    title: 'Vendor Schemas',
    icon: Package,
    subtitle: 'Define what the vendor expects',
    description: `
      <p>Specifies the expected data format for each vendor integration. It doesn’t enforce anything — just clearly defines structure so Transforms can map to it accurately.</p>
    `,
    link: '/labs/starter-kits',
    jsonSchemaRelated: true,
    toolLabel: 'Vendor Schemas',
    toolIcon: Package,
    toolType: 'JSON Schema Editor',
  },
];

const SchemaDiagram: React.FC = () => {
  const [activeStepId, setActiveStepId] = useState<string | null>('customer-schema');

  const activeStep = diagramSteps.find(step => step.id === activeStepId);

  const DiagramNode: React.FC<{ step: DiagramStep }> = ({ step }) => (
    <div
      className={clsx(
        "flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer transition-all duration-300 min-h-[8rem]",
        "border-2",
        "flex-1 max-w-[8rem]", // Allow flex item to grow and shrink, but cap max width
        activeStepId === step.id
          ? "border-blue-500 shadow-lg bg-blue-50 dark:bg-blue-900/20" // Lighter highlight for active
          : "border-transparent hover:border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700", // Lighter highlight for hover
      )}
      onClick={() => setActiveStepId(step.id)}
    >
      <step.icon
        className={clsx(
          "mb-2",
          activeStepId === step.id ? "text-blue-500" : step.jsonSchemaRelated ? "text-blue-600" : "text-green-600",
        )}
        size={24} // Explicitly set icon size
      />
      <span
        className={clsx(
          "text-center font-semibold text-[15px]",
          activeStepId === step.id ? "text-blue-500" : "text-gray-800 dark:text-white",
        )}
      >
        {step.title}
      </span>
    </div>
  );

  const FlowArrow: React.FC = () => (
    <div className="flex items-center justify-center w-8"> {/* Reduced width for less space */}
      <ArrowRight className="text-gray-400 dark:text-gray-600" size={24} /> {/* Explicitly set icon size to match main icons */}
    </div>
  );

  return (
    <div className="flex flex-col gap-7">
      {/* Diagram Section */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="flex flex-wrap items-center justify-center pt-8 gap-x-2 gap-y-4">

         

          {/* Step 1: Customer Schema */}
          <div className="flex flex-col items-center"> {/* Removed mx-4 */}
            <DiagramNode step={diagramSteps[0]} />
          </div>
          <FlowArrow />

          {/* Step 2: Pipeline Transforms */}
          <div className="flex flex-col items-center"> {/* Removed mx-4 */}
            <DiagramNode step={diagramSteps[1]} />
          </div>
          <FlowArrow />

          {/* Step 3: MetaRouter Schema (Optional) */}
          <div className="flex flex-col items-center"> {/* Removed mx-4 */}
            <DiagramNode step={diagramSteps[2]} />
          </div>
          <FlowArrow />

          {/* Step 4: Vendor Transforms */}
          <div className="flex flex-col items-center"> {/* Removed mx-4 */}
            <DiagramNode step={diagramSteps[3]} />
          </div>
          <FlowArrow />

          {/* Step 5: Vendor Schemas */}
          <div className="flex flex-col items-center"> {/* Removed mx-4 */}
            <DiagramNode step={diagramSteps[4]} />
          </div>
        </div>
      </div>

      {/* Explanation Section */}
      <div className="w-full rounded-lg flex flex-col justify-between h-[400px]">
        {activeStep ? (
          <div>
            <span className={clsx(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-medium mb-2",
              activeStep.toolType === 'YAML Playbook Editor'
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            )}>
              {activeStep.toolType}
            </span>            
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{activeStep.subtitle}</h3>
            <div
              className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-4 px-24"
              dangerouslySetInnerHTML={{ __html: activeStep.description }}
            />
          </div>
        ) : (
          <div className="text-gray-500 dark:text-gray-400 text-center py-10">
            <p>Click on a diagram section to learn more.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemaDiagram;
